import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@jbnl/api-client";
import { api } from "@/lib/api";
import i18n from "@/i18n";

export const synchronisatiesKey = ["beheer", "synchronisaties"] as const;

export type Synchronisatie = components["schemas"]["SynchronisatieDto"];

/**
 * The statuses the API answers, as names rather than numbers - see `SynchronisatieDto.Status`.
 * A null status is a bron that has never run in this environment.
 */
export const STATUS = {
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
} as const;

export const BRON = { portaal: "Portaal", vbs: "Vbs" } as const;

export type Bron = (typeof BRON)[keyof typeof BRON];

/** The path names the bron in lower case; the DTO names it in the enum's own casing. */
const pad = (bron: Bron) => bron.toLowerCase();

/** The API answers RFC 7807, so a refusal carries a Dutch sentence the beheerder can act on. */
function problem(error: unknown, response: Response, fallback: string) {
  const title = (error as { title?: string } | undefined)?.title;
  return new Error(title ?? `${fallback} (${response.status}).`);
}

/**
 * Both syncs, and whether a jobs service is there to run them.
 *
 * It polls while one is running, because the run happens in the jobs service and a full read of JBNL
 * Portaal takes minutes. Five seconds rather than the two the wedstrijdboekjes use: nothing here
 * finishes in two.
 */
export function useSynchronisaties() {
  return useQuery({
    queryKey: synchronisatiesKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/beheer/synchronisaties");
      if (!data) throw problem(error, response, i18n.t("synchronisaties:errors.ophalen"));
      return data;
    },
    refetchInterval: (query) =>
      query.state.data?.bronnen.some((sync) => sync.status === STATUS.running) ? 5000 : false,
    // The run keeps going while a beheerder looks at something else, and they come back to a card
    // that should already say how it ended.
    refetchIntervalInBackground: true,
  });
}

/** Starts one of the two syncs. The API refuses a press it cannot honour, naming which. */
export function useStartSynchronisatie() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bron: Bron) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/synchronisaties/{bron}/start",
        { params: { path: { bron: pad(bron) } } },
      );
      if (!data) throw problem(error, response, i18n.t("synchronisaties:errors.starten"));
      return data;
    },
    // Invalidated rather than written from the answer: the answer is one bron, the screen holds both,
    // and the poll above is what has to start.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: synchronisatiesKey }),
  });
}
